import { useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { screenshotApi } from "../api/screenshots";
import {
  monitorApi,
  type CreateMonitorRequest,
  type AreaCoordinates,
} from "../api/monitors";
import AreaSelector from "./AreaSelector";

const MonitorCreator = () => {
  const [url, setUrl] = useState("uptimerobot.com");
  const [email, setEmail] = useState("");
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [showEmailSection, setShowEmailSection] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showAreaSelector, setShowAreaSelector] = useState(false);
  const [areaCoordinates, setAreaCoordinates] =
    useState<AreaCoordinates | null>(null);

  // Screenshot capture mutation
  const screenshotMutation = useMutation({
    mutationFn: (urlToCapture: string) => screenshotApi.capture(urlToCapture),
    onSuccess: (data) => {
      setScreenshot(data.screenshot);
      setShowAreaSelector(true);
      setShowEmailSection(true);
      setErrorMessage("");
    },
    onError: (error: Error) => {
      setErrorMessage(error.message || "Failed to generate screenshot");
      setScreenshot(null);
      setShowEmailSection(false);
      setShowAreaSelector(false);
    },
  });

  // Monitor creation mutation
  const monitorMutation = useMutation({
    mutationFn: async (data: CreateMonitorRequest) => {
      return await monitorApi.create(data);
    },
    onSuccess: () => {
      window.scrollTo(0, 0);
      setShowSuccess(true);
      setShowEmailSection(false);
      setEmail("");
      setErrorMessage("");
    },
    onError: (error: Error) => {
      setErrorMessage(error.message || "Failed to create monitor");
    },
  });

  const validateUrl = (
    urlString: string
  ): { isValid: boolean; processedUrl?: string; error?: string } => {
    if (!urlString.trim()) {
      return { isValid: false, error: "Please enter a URL" };
    }

    let processedUrl = urlString.trim();

    // Add https:// if not present
    if (!/^https?:\/\//i.test(processedUrl)) {
      processedUrl = "https://" + processedUrl;
    }

    try {
      const url = new URL(processedUrl);

      // Only allow HTTP/HTTPS protocols
      if (url.protocol !== "http:" && url.protocol !== "https:") {
        return {
          isValid: false,
          error: "Only HTTP and HTTPS URLs are allowed",
        };
      }

      // Must have a hostname (FQDN requirement)
      if (!url.hostname) {
        return { isValid: false, error: "Please enter a valid domain name" };
      }

      // Must contain at least one dot (basic FQDN check)
      if (!url.hostname.includes(".")) {
        return {
          isValid: false,
          error: "URL must include a valid domain name (e.g., example.com)",
        };
      }

      // Hostname must not contain invalid characters and be reasonable length
      const hostnameRegex = /^[a-zA-Z0-9.-]+$/;
      if (!hostnameRegex.test(url.hostname) || url.hostname.length > 253) {
        return { isValid: false, error: "Please enter a valid domain name" };
      }

      // Must not be an IP address (we want FQDN only)
      const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
      const ipv6Regex = /^\[?([a-fA-F0-9:]+)\]?$/;
      if (ipv4Regex.test(url.hostname) || ipv6Regex.test(url.hostname)) {
        return {
          isValid: false,
          error: "Only domain names are allowed (no IP addresses)",
        };
      }

      // Don't allow localhost
      if (url.hostname === "localhost" || url.hostname === "127.0.0.1") {
        return { isValid: false, error: "Local URLs are not allowed" };
      }

      return { isValid: true, processedUrl };
    } catch (e) {
      return { isValid: false, error: "Please enter a valid URL" };
    }
  };

  const handleTakeScreenshot = () => {
    setErrorMessage("");

    // Reset states when taking a new screenshot
    setScreenshot(null);
    setShowAreaSelector(false);
    setShowEmailSection(false);
    setAreaCoordinates(null);
    setEmail("");

    const validation = validateUrl(url);
    if (!validation.isValid) {
      setErrorMessage(validation.error!);
      return;
    }

    screenshotMutation.mutate(validation.processedUrl!);
  };

  const handleCreateMonitor = () => {
    setErrorMessage("");

    if (!email.trim()) {
      setErrorMessage("Please enter an email address");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setErrorMessage("Please enter a valid email address");
      return;
    }

    const validation = validateUrl(url);
    if (!validation.isValid) {
      setErrorMessage(validation.error!);
      return;
    }

    monitorMutation.mutate({
      email: email.trim(),
      area_coordinates: areaCoordinates || undefined,
      url: validation.processedUrl!,
    });
  };

  const handleAreaSelection = (area: AreaCoordinates) => {
    setAreaCoordinates(area);
  };

  const handleKeyPress = (
    e: React.KeyboardEvent,
    action: "screenshot" | "monitor"
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (action === "screenshot") {
        handleTakeScreenshot();
      } else {
        handleCreateMonitor();
      }
    }
  };

  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        setShowSuccess(false);
        setScreenshot(null);
        setShowAreaSelector(false);
        setShowEmailSection(false);
        setAreaCoordinates(null);
        setEmail("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  if (showSuccess) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="bg-white rounded-lg w-full max-w-[900px] shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.1),0px_10px_10px_-5px_rgba(0,0,0,0.04)] p-8">
          <div className="border border-[#e2e9f0] rounded-lg min-h-[316px] flex flex-col items-center justify-center gap-3 p-8">
            <svg width="100" height="100" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M60 110C32.385 110 10 87.615 10 60C10 32.385 32.385 10 60 10C87.615 10 110 32.385 110 60C110 87.615 87.615 110 60 110ZM60 100C70.6087 100 80.7828 95.7857 88.2843 88.2843C95.7857 80.7828 100 70.6087 100 60C100 49.3913 95.7857 39.2172 88.2843 31.7157C80.7828 24.2143 70.6087 20 60 20C49.3913 20 39.2172 24.2143 31.7157 31.7157C24.2143 39.2172 20 49.3913 20 60C20 70.6087 24.2143 80.7828 31.7157 88.2843C39.2172 95.7857 49.3913 100 60 100ZM55.015 80L33.8 58.785L40.87 51.715L55.015 65.86L83.295 37.575L90.37 44.645L55.015 80Z" fill="#3BD671"/>
            </svg>
            <h2 className="text-[#1c2834] text-xl font-bold text-center">
              Content monitor has been successfully created
            </h2>
            <p className="text-[#a0b0c0] text-base text-center">
              You'll get shortly redirected to the first page
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isUrlFilled = url.trim();
  const isEmailFilled = email.trim();

  return (
    <div className="flex justify-center items-center">
      <div className="bg-white rounded-lg w-full max-w-[900px] shadow-xl p-8">
        {/* URL Input Section */}
        <div className="flex mb-5 gap-2.5">
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyPress={(e) => handleKeyPress(e, "screenshot")}
            placeholder="eg. adidas.com/sale"
            className="flex-1 rounded-xl border border-gray-200 outline-none text-lg px-5 h-[48px] bg-white focus:border-gray-300 transition-colors text-gray-900"
          />
          <button
            onClick={handleTakeScreenshot}
            disabled={screenshotMutation.isPending}
            className={`text-white border-none px-4 rounded-xl text-[16px] font-medium h-[48px] w-44 transition-colors ${
              screenshotMutation.isPending ? 'bg-gray-400 cursor-not-allowed' :
              isUrlFilled ? 'bg-neutral-700 hover:bg-neutral-800 cursor-pointer' : 'bg-gray-400 hover:bg-gray-500 cursor-pointer'
            }`}
          >
            {screenshotMutation.isPending ? "Checking..." : "Check"}
          </button>
        </div>

        {/* Content Area */}
        <div>
          {/* Placeholder */}
          {!screenshot && !screenshotMutation.isPending && (
            <div className="rounded-lg text-center min-h-[318px] flex flex-col justify-center items-center border border-gray-200 p-5">
              {/* <svg
                className="opacity-50 mb-5 w-20 h-20"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polygon points="21 15 16 10 5 21" />
              </svg> */}
              <svg
                width="100"
                height="90"
                viewBox="0 0 100 90"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M90 10H10V80L56.46 33.53C57.3976 32.5926 58.6692 32.0661 59.995 32.0661C61.3208 32.0661 62.5924 32.5926 63.53 33.53L90 60.05V10ZM0 4.965C0.00915022 3.65189 0.534501 2.39509 1.46256 1.46609C2.39063 0.53709 3.6469 0.0104732 4.96 0H95.04C97.78 0 100 2.225 100 4.965V85.035C99.9908 86.3481 99.4655 87.6049 98.5374 88.5339C97.6094 89.4629 96.3531 89.9895 95.04 90H4.96C3.64407 89.9987 2.38248 89.475 1.45245 88.544C0.52241 87.613 -6.67276e-07 86.3509 0 85.035V4.965ZM30 40C27.3478 40 24.8043 38.9464 22.9289 37.0711C21.0536 35.1957 20 32.6522 20 30C20 27.3478 21.0536 24.8043 22.9289 22.9289C24.8043 21.0536 27.3478 20 30 20C32.6522 20 35.1957 21.0536 37.0711 22.9289C38.9464 24.8043 40 27.3478 40 30C40 32.6522 38.9464 35.1957 37.0711 37.0711C35.1957 38.9464 32.6522 40 30 40Z"
                  fill="#CBD6E0"
                />
              </svg>

              <div className="text-gray-400 text-lg mt-[27px]">
                Enter website to see snapshot
              </div>
            </div>
          )}

          {/* Loading */}
          {screenshotMutation.isPending && (
            <div className="text-center py-10 min-h-[318px] flex flex-col justify-center items-center">
              <div className="rounded-full animate-spin mx-auto mb-5 border-4 border-gray-200 border-t-blue-500 w-8 h-8"></div>
              <div>Generating screenshot...</div>
            </div>
          )}

          {/* Screenshot with Area Selection */}
          {screenshot && !showAreaSelector && (
            <div className="rounded-lg overflow-hidden mb-5">
              <img
                src={`data:image/jpeg;base64,${screenshot}`}
                alt="Website Screenshot"
                className="max-w-full block"
              />
              {areaCoordinates && (
                <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                  Area selected: {areaCoordinates.percent_width}%×
                  {areaCoordinates.percent_height}% at (
                  {areaCoordinates.percent_x}%, {areaCoordinates.percent_y}%)
                </div>
              )}
            </div>
          )}

          {/* Area Selection */}
          {screenshot && showAreaSelector && (
            <div>
              <AreaSelector
                imageSrc={`data:image/jpeg;base64,${screenshot}`}
                onCropChange={handleAreaSelection}
              />
            </div>
          )}

          {/* Email Section */}
          {showEmailSection && (
            <div className="mt-5">
              <h3 className="text-gray-700 font-bold text-sm mb-3">Send alerts to email</h3>
              <div className="flex mb-5 gap-2.5">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, "monitor")}
                  placeholder="e.g. info@example.com"
                  className="flex-1 rounded-xl border border-gray-200 outline-none text-base px-5 py-3 bg-white focus:border-gray-300 transition-colors text-gray-900"
                />
                <button
                  onClick={handleCreateMonitor}
                  disabled={monitorMutation.isPending || !email.trim()}
                  className={`text-white border-0 rounded-xl text-base font-medium block py-4 px-8 transition-colors ${
                    monitorMutation.isPending ? 'bg-gray-400 cursor-not-allowed' :
                    isEmailFilled ? 'bg-neutral-700 hover:bg-neutral-800 cursor-pointer' : 'bg-gray-400 hover:bg-gray-500 cursor-pointer'
                  }`}
                >
                  {monitorMutation.isPending ? "Creating..." : "Go"}
                </button>
              </div>
              <div className="text-right font-medium mt-2.5 text-[10px] text-gray-400">
                By submitting your email you will automatically agree to our t&c
              </div>
            </div>
          )}

          {/* Error Message */}
          {errorMessage && (
            <div className="text-center mt-2.5 text-red-600 p-4">
              {errorMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonitorCreator;
